// Detection API client — Tier 0 surface (see docs/SCAN-ROADMAP.md).
// Routes mirror /api/v1/detection/* on the backend.
//
// Tier 0 returns mostly empty data: no scanners are enabled in the
// in-process registry yet, so scan_jobs rows stay 'skipped' and the
// findings list is empty. The client surface lives here in full so
// Tier 1 can flip the scanner enabled flag and the UI populates
// without further client-layer changes.

import useSWR from 'swr';
import { parseErrorResponse } from './problem';

export type FindingSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';
export type FindingStatus = 'open' | 'acknowledged' | 'suppressed' | 'fixed';

export interface Finding {
  id: string;
  sha256: string;
  artifact_id: string;
  artifact_path: string;
  repo_slug: string;
  project_slug: string;
  project_id: string;
  format: string;
  scanner_id: string;
  rule_id: string;
  severity: FindingSeverity;
  status: FindingStatus;
  title: string;
  detail: string;
  location?: string;
  fix_hint?: string;
  evidence?: Record<string, unknown>;
  first_seen_at: string;
  last_seen_at: string;
  suppressed_at?: string | null;
  suppressed_by?: string;
  suppress_reason?: string;
  // v181 Tier 5 SLA tracking. age_days is computed server-side
  // (PG `EXTRACT(EPOCH FROM now() - first_seen_at) / 86400`).
  age_days?: number;
  sla_breached_at?: string | null;
}

export interface ScanRules {
  project_id: string;
  enabled: boolean;
  fail_on: FindingSeverity;
  scanner_set: string[];
  path_excludes: string[];
  suppressions: Suppression[];
  // v169 Tier 2: per-scanner JSON config keyed by scanner_id.
  // Documented shapes:
  //   orbital.license.v1:    { allow: string[] }    // SPDX IDs
  //   orbital.banned-api.v1: { patterns: BannedApiPattern[] }
  //   orbital.secret.v1:     { extra_patterns: SecretPattern[] }
  scanner_config?: Record<string, Record<string, unknown>>;
  // v181 Tier 5: per-severity time-to-fix in days. Missing keys mean
  // "no SLA, never breach" — useful for muting `info` entirely.
  sla_targets?: Partial<Record<FindingSeverity, number>>;
}

export interface BannedApiPattern {
  rule_id: string;
  title?: string;
  regex: string;
  severity: FindingSeverity;
  message?: string;
  extensions?: string[];
}

export interface SecretExtraPattern {
  rule_id: string;
  title: string;
  regex: string;
  severity: FindingSeverity;
}

export interface Suppression {
  rule_id: string;
  reason: string;
  until?: string;
}

export interface Scanner {
  id: string;
  kind: string;
  version: string;
  rules_version: string;
  enabled: boolean;
  description: string;
  cost_class: 'cheap' | 'medium' | 'heavy';
}

export interface QueueStats {
  pending: number;
  claimed: number;
  running: number;
  done: number;
  failed: number;
  skipped: number;
  oldest_pending_sec: number;
}

const json = async (url: string) => {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw await parseErrorResponse(r);
  return r.json();
};

export interface FindingFilters {
  status?: FindingStatus;
  severity?: FindingSeverity;
  project?: string;
  scanner?: string;
  limit?: number;
}

function buildFindingsURL(f?: FindingFilters): string {
  const u = new URL('/api/v1/detection/findings', globalThis.location.origin);
  if (f?.status) u.searchParams.set('status', f.status);
  if (f?.severity) u.searchParams.set('severity', f.severity);
  if (f?.project) u.searchParams.set('project', f.project);
  if (f?.scanner) u.searchParams.set('scanner', f.scanner);
  if (f?.limit) u.searchParams.set('limit', String(f.limit));
  return u.pathname + u.search;
}

export function useFindings(filters?: FindingFilters) {
  return useSWR<Finding[]>(buildFindingsURL(filters), json, {
    refreshInterval: 30_000,
  });
}

export function useFinding(id: string | undefined) {
  return useSWR<Finding>(
    id ? `/api/v1/detection/findings/${id}` : null,
    json,
  );
}

export async function patchFindingStatus(
  id: string,
  status: FindingStatus,
  suppressReason?: string,
): Promise<void> {
  const res = await fetch(`/api/v1/detection/findings/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, suppress_reason: suppressReason ?? '' }),
  });
  if (!res.ok) throw await parseErrorResponse(res);
}

export function useScanRules(projectID: string | undefined) {
  return useSWR<ScanRules>(
    projectID ? `/api/v1/detection/rules?project=${encodeURIComponent(projectID)}` : null,
    json,
  );
}

export async function saveScanRules(projectID: string, rules: Omit<ScanRules, 'project_id'>): Promise<void> {
  const res = await fetch(
    `/api/v1/detection/rules?project=${encodeURIComponent(projectID)}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rules),
    },
  );
  if (!res.ok) throw await parseErrorResponse(res);
}

export function useScanners() {
  return useSWR<Scanner[]>('/api/v1/detection/scanners', json);
}

export function useQueueStats() {
  return useSWR<QueueStats>('/api/v1/detection/queue', json, {
    refreshInterval: 10_000,
  });
}

export async function enqueueRescan(input: {
  artifact_id: string;
  scanners?: string[];
}): Promise<{ enqueued: number }> {
  const res = await fetch('/api/v1/detection/jobs', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}

// runBackfill enqueues every (artifact, enabled-scanner) pair that
// doesn't already have a queue row, at backlog priority. Admin-only;
// returns the count enqueued. Optionally project-scoped.
export async function runBackfill(projectID?: string): Promise<{ enqueued: number; project: string; priority: number }> {
  const url = projectID
    ? `/api/v1/detection/backfill?project=${encodeURIComponent(projectID)}`
    : '/api/v1/detection/backfill';
  const res = await fetch(url, { method: 'POST', credentials: 'include' });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}

// --- v177 Tier 4 ---

export interface StatsRow {
  key: string;
  label?: string;
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

export function useStatsByCVE() {
  return useSWR<StatsRow[]>('/api/v1/detection/stats/by-cve', json, { refreshInterval: 60_000 });
}

export function useStatsByProject() {
  return useSWR<StatsRow[]>('/api/v1/detection/stats/by-project', json, { refreshInterval: 60_000 });
}

export function useStatsByPackage() {
  return useSWR<StatsRow[]>('/api/v1/detection/stats/by-package', json, { refreshInterval: 60_000 });
}

export interface FindingEvent {
  id: number;
  kind: 'opened' | 'acknowledged' | 'suppressed' | 'fixed' | 'reopened' | 'auto-reopened';
  severity: FindingSeverity;
  actor: string;
  reason: string;
  until?: string;
  occurred_at: string;
}

export function useFindingEvents(id: string | undefined) {
  return useSWR<FindingEvent[]>(id ? `/api/v1/detection/findings/${id}/events` : null, json);
}

// bulkPatchFindings patches many findings in a single call.
export async function bulkPatchFindings(input: {
  ids: string[];
  status: FindingStatus;
  suppress_reason?: string;
  suppressed_until?: string; // RFC3339 or undefined = indefinite
}): Promise<{ requested: number; matched: number; updated: number }> {
  const res = await fetch('/api/v1/detection/findings', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}

// patchFinding extends the v161 single-PATCH path with the v177
// suppressed_until + reason fields. Status-only callers can omit
// the optional fields.
export async function patchFinding(id: string, input: {
  status: FindingStatus;
  suppress_reason?: string;
  suppressed_until?: string;
}): Promise<void> {
  const res = await fetch(`/api/v1/detection/findings/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorResponse(res);
}

export function vexExportURL(projectIDOrSlug: string): string {
  return `/api/v1/detection/vex?project=${encodeURIComponent(projectIDOrSlug)}`;
}

// v181 Tier 5 finding diff vs the previous version of the same
// (repo, path) artifact line.
export interface FindingDiffEntry {
  scanner_id: string;
  rule_id: string;
  severity: FindingSeverity;
  title: string;
  location?: string;
}

export interface FindingDiff {
  current_version?: string;
  previous_version?: string;
  added: FindingDiffEntry[];
  persisted: FindingDiffEntry[];
  resolved: FindingDiffEntry[];
}

export function useFindingDiff(id: string | undefined) {
  return useSWR<FindingDiff>(
    id ? `/api/v1/detection/findings/${id}/diff` : null,
    json,
  );
}

// Aggregated vulnerabilities (item 334): one row per canonical vuln_key.
export interface VulnerabilityRow {
  vuln_key: string;
  title: string;
  severity: FindingSeverity;
  scanners: string[];
  artifact_count: number;
  package_count: number;
  finding_count: number;
  fix_versions: string[];
}

export interface VulnerabilitiesPage {
  items: VulnerabilityRow[];
  next_cursor?: string;
}

export interface VulnArtifact {
  artifact_id: string;
  path: string;
  version: string;
  scanners: string[];
  severity: FindingSeverity;
  packages: string[];
  fix_versions: string[];
}

export interface VulnGroup {
  repository: string;
  base_layer?: string;
  artifacts: VulnArtifact[];
}

export interface VulnerabilityDetail {
  vuln_key: string;
  title: string;
  severity: FindingSeverity;
  scanners: string[];
  truncated?: boolean;
  groups: VulnGroup[];
}

export interface VulnerabilityFilters {
  status?: FindingStatus;
  severity?: FindingSeverity;
  project?: string;
  scanner?: string;
}

export function buildVulnerabilitiesURL(f?: VulnerabilityFilters, cursor?: string): string {
  const u = new URL('/api/v1/detection/vulnerabilities', globalThis.location.origin);
  if (f?.status) u.searchParams.set('status', f.status);
  if (f?.severity) u.searchParams.set('severity', f.severity);
  if (f?.project) u.searchParams.set('project', f.project);
  if (f?.scanner) u.searchParams.set('scanner', f.scanner);
  if (cursor) u.searchParams.set('cursor', cursor);
  return u.pathname + u.search;
}

export function useVulnerabilities(url: string) {
  return useSWR<VulnerabilitiesPage>(url, json, { refreshInterval: 60_000 });
}

export function useVulnerability(
  key: string | undefined,
  f?: Pick<VulnerabilityFilters, 'status' | 'project' | 'scanner'>,
) {
  let url: string | null = null;
  if (key) {
    const u = new URL(
      `/api/v1/detection/vulnerabilities/${encodeURIComponent(key)}`,
      globalThis.location.origin,
    );
    if (f?.status) u.searchParams.set('status', f.status);
    if (f?.project) u.searchParams.set('project', f.project);
    if (f?.scanner) u.searchParams.set('scanner', f.scanner);
    url = u.pathname + u.search;
  }
  return useSWR<VulnerabilityDetail>(url, json);
}

// patchVulnerability triages every finding of one vuln_key (item 334 step 5).
// The filters narrow which findings are touched, like the drill-down's.
// `truncated` means the 5000-finding cap hit: repeat until it is false.
export async function patchVulnerability(
  key: string,
  input: { status: FindingStatus; suppress_reason?: string; suppressed_until?: string },
  f?: Pick<VulnerabilityFilters, 'status' | 'project' | 'scanner'>,
): Promise<{ requested: number; matched: number; updated: number; truncated?: boolean }> {
  const u = new URL(
    `/api/v1/detection/vulnerabilities/${encodeURIComponent(key)}`,
    globalThis.location.origin,
  );
  if (f?.status) u.searchParams.set('status', f.status);
  if (f?.project) u.searchParams.set('project', f.project);
  if (f?.scanner) u.searchParams.set('scanner', f.scanner);
  const res = await fetch(u.pathname + u.search, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}
